import MuiAutocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteValue,
  AutocompleteProps as MuiAutocompleteProps,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import React, { useCallback, useMemo } from 'react';

export type AutocompleteProps<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
> = MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>;

export const Autocomplete = <
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
>({
  options,
  value,
  onChange,
  ...props
}: Omit<
  MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'options' | 'value' | 'onChange'
> & {
  options: readonly (T | Partial<T> | { [K in keyof T]?: T[K] | null })[];
  value?:
    | AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
    | Partial<T>
    | { [K in keyof T]?: T[K] | null };
  onChange?: (...args: any[]) => void;
}) => {
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Stop propagation to prevent parent components (like Menu) from capturing keypresses
    event.stopPropagation();
  }, []);

  return (
    <MuiAutocomplete
      options={options as readonly T[]}
      value={
        value as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
      }
      onChange={
        onChange as MuiAutocompleteProps<
          T,
          Multiple,
          DisableClearable,
          FreeSolo
        >['onChange']
      }
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
};

type AddableAutocompleteProps<
  T extends { title: string; inputValue?: string },
  Multiple extends boolean = false,
  DisableClearable extends boolean = false
> = AutocompleteProps<T, Multiple, DisableClearable, true> & {
  onAddItem?: (inputValue: string) => void | Promise<void>;
};

export const AddableAutocomplete = <
  T extends { title: string; inputValue?: string; id?: string },
  Multiple extends boolean = false,
  DisableClearable extends boolean = false
>({
  onAddItem,
  onChange,
  ...props
}: AddableAutocompleteProps<T, Multiple, DisableClearable>) => {
  const filter = useMemo(() => createFilterOptions<T>(), []);

  const handleChange = useCallback(
    async (
      event: React.SyntheticEvent,
      value: AutocompleteValue<T, Multiple, DisableClearable, true>,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<T>
    ) => {
      // Handle the "Add" option
      if (value && typeof value === 'object' && 'inputValue' in value) {
        await onAddItem?.(value.inputValue as string);
      }
      onChange?.(event, value, reason, details);
    },
    [onAddItem, onChange]
  );

  const filterOptions = useCallback(
    (options: readonly T[], params: any) => {
      const filtered = filter([...options], params);

      const { inputValue } = params;
      const isExisting = options.some((option) => inputValue === option.title);
      if (inputValue !== '' && !isExisting) {
        filtered.push({
          inputValue,
          title: `Add "${inputValue}"`,
        } as T);
      }
      return filtered;
    },
    [filter]
  );

  const getOptionLabel = useCallback((option: T | string) => {
    if (!option) return '';
    if (typeof option === 'string') {
      return option;
    }
    if (option.inputValue) {
      return option.inputValue;
    }
    return option.title;
  }, []);

  const renderOption = useCallback((props: any, option: T) => {
    const { key, ...optionProps } = props;
    // Create a stable key - use option.id if available, otherwise use inputValue for "Add" options
    const stableKey = option.id
      ? `${key}-${option.id}`
      : option.inputValue
      ? `${key}-add-${option.inputValue}`
      : key;

    return (
      <li key={stableKey} {...optionProps}>
        {option.title}
      </li>
    );
  }, []);

  return (
    <Autocomplete
      {...props}
      onChange={handleChange}
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
    />
  );
};
